import {
  Alert,
  Button,
  Card,
  Col,
  Image,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import { useEffect, useState } from "react";
import {
  CheckOutlined,
  CloseOutlined,
  EyeOutlined,
  GiftOutlined,
  InfoCircleOutlined,
  ReloadOutlined,
  SearchOutlined,
  SendOutlined,
} from "@ant-design/icons";
import Search from "antd/es/input/Search";
import { apiCall } from "../../API/RestApi";
import { useSelector } from "react-redux";
import dayjs from "dayjs";

const { Text } = Typography;

function PrizeClaim() {
  const token = useSelector((state) => state.user.token);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");

  const [isAlert, setIsAlert] = useState(false);
  const [textAlert, setTextAlert] = useState("");

  const [page, setPage] = useState({
    pagination: {
      current: 1,
      pageSize: 10,
    },
  });

  const headers = {
    Authorization: "Bearer " + token,
  };

  const getStatusTag = (value) => {
    if (value === "pending") return <Tag color="orange">Pending</Tag>;
    if (value === "approved") return <Tag color="blue">Approved</Tag>;
    if (value === "rejected") return <Tag color="red">Rejected</Tag>;
    if (value === "delivered") return <Tag color="green">Delivered</Tag>;
    return <Tag>{value || "-"}</Tag>;
  };

  const formatDate = (value) => {
    return value ? dayjs(value).format("YYYY-MM-DD HH:mm:ss") : "-";
  };

  const getData = () => {
    setLoading(true);

    let url =
      "/prize-claim?page=" +
      page.pagination.current +
      "&limit=" +
      page.pagination.pageSize;

    if (status !== "") {
      url += "&status=" + status;
    }

    apiCall({
      method: "GET",
      url,
      data: { headers },
    })
      .then((res) => {
        if (res.status === 200) {
          let rows = res.data.rows || [];

          if (search !== "") {
            rows = rows.filter((item) => {
              const keyword = search.toLowerCase();

              return (
                String(item.claim_code || "").toLowerCase().includes(keyword) ||
                String(item.user?.name || "").toLowerCase().includes(keyword) ||
                String(item.user?.Email || "").toLowerCase().includes(keyword) ||
                String(item.user?.mobile || "").toLowerCase().includes(keyword) ||
                String(item.prize?.name || "").toLowerCase().includes(keyword)
              );
            });
          }

          setData(rows);

          setPage({
            ...page,
            pagination: {
              ...page.pagination,
              total: res.data.count || 0,
            },
          });
        }

        setLoading(false);
      })
      .catch((e) => {
        console.log(e);
        setLoading(false);
      });
  };

  useEffect(() => {
    getData();
  }, [JSON.stringify(page), status, search]);

  const updateStatus = (record, newStatus) => {
    Modal.confirm({
      title: "Update Status Claim",
      content: `Ubah status claim "${record.claim_code}" menjadi "${newStatus}"?`,
      okText: "Update",
      cancelText: "Batal",
      onOk: () => {
        setLoading(true);

        apiCall({
          method: "PUT",
          url: "/prize-claim",
          data: {
            data: {
              prize_claim_id: record.prize_claim_id,
              status: newStatus,
            },
            headers,
          },
        })
          .then((res) => {
            setLoading(false);

            if (res.status === 200) {
              setIsAlert(true);
              setTextAlert(res.message || "Status berhasil diupdate");
              getData();
            } else {
              Modal.error({
                title: "Gagal",
                content: res.message,
              });
            }
          })
          .catch((e) => {
            console.log(e);
            setLoading(false);
          });
      },
    });
  };

  const openDetail = (record) => {
    Modal.info({
      title: "Detail Prize Claim",
      width: 700,
      content: (
        <div style={{ marginTop: 12 }}>
          {record.prize?.image && (
            <Image
              width={180}
              src={record.prize.image}
              style={{
                borderRadius: 8,
                marginBottom: 16,
              }}
            />
          )}

          <p><b>Claim Code:</b> {record.claim_code}</p>
          <p><b>Status:</b> {record.status}</p>
          <p><b>Prize:</b> {record.prize?.name || "-"}</p>
          <p><b>Description:</b> {record.prize?.description || "-"}</p>
          <p><b>Price:</b> Rp {Number(record.prize?.price || 0).toLocaleString("id-ID")}</p>

          <hr />
          {console.log(record)}
          <p><b>Name:</b> {record.user?.name || "-"}</p>
          <p><b>Email:</b> {record.user?.Email || "-"}</p>
          <p><b>Mobile:</b> {record.user?.mobile || "-"}</p>

          <hr />

          <p><b>Drawn At:</b> {formatDate(record.draw_session?.drawn_at)}</p>
          <p><b>IP Address:</b> {record.draw_session?.ip_address || "-"}</p>
          <p><b>User Agent:</b> {record.draw_session?.user_agent || "-"}</p>

          <hr />

          <p><b>Claimed At:</b> {formatDate(record.claimed_at)}</p>
          <p><b>Delivered At:</b> {formatDate(record.delivered_at)}</p>
        </div>
      ),
    });
  };

  const columns = [
    {
      title: "No",
      key: "no",
      width: 70,
      render: (_, record, index) =>
        (page.pagination.current - 1) * page.pagination.pageSize + index + 1,
    },
    {
      title: "Claim Code",
      dataIndex: "claim_code",
      key: "claim_code",
      width: 240,
      render: (text) => <b>{text}</b>,
    },
    {
      title: "Prize",
      key: "prize",
      width: 260,
      render: (_, record) => (
        <Space>
          {record.prize?.image && (
            <Image
              width={45}
              height={45}
              src={record.prize.image}
              style={{ objectFit: "cover", borderRadius: 6 }}
            />
          )}
          <div>
            <b>{record.prize?.name || "-"}</b>
            <br />
            <Text type="secondary">
              Rp {Number(record.prize?.price || 0).toLocaleString("id-ID")}
            </Text>
          </div>
        </Space>
      ),
    },
    {
      title: "Customer",
      key: "customer",
      width: 260,
      render: (_, record) => (
        <div>
          <b>{record.user?.name || "-"}</b>
          <br />
          <Text type="secondary">{record.user?.Email || "-"}</Text>
          <br />
          <Text type="secondary">{record.user?.mobile || "-"}</Text>
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 130,
      render: getStatusTag,
    },
    {
      title: "Claimed At",
      dataIndex: "claimed_at",
      key: "claimed_at",
      width: 180,
      render: formatDate,
    },
    {
      title: "Delivered At",
      dataIndex: "delivered_at",
      key: "delivered_at",
      width: 180,
      render: formatDate,
    },
    {
      title: "Action",
      key: "action",
      width: 260,
      fixed: "right",
      render: (_, record) => (
        <Space>
          <Tooltip title="Detail">
            <Button icon={<EyeOutlined />} onClick={() => openDetail(record)} />
          </Tooltip>

          <Tooltip title="Approve">
            <Button
              type="primary"
              icon={<CheckOutlined />}
              disabled={record.status !== "pending"}
              onClick={() => updateStatus(record, "approved")}
            />
          </Tooltip>

          <Tooltip title="Reject">
            <Button
              danger
              icon={<CloseOutlined />}
              disabled={record.status !== "pending"}
              onClick={() => updateStatus(record, "rejected")}
            />
          </Tooltip>

          <Tooltip title="Delivered">
            <Button
              icon={<SendOutlined />}
              disabled={record.status !== "approved"}
              onClick={() => updateStatus(record, "delivered")}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const handleTableChange = (pagination, filters, sorter) => {
    setPage({
      pagination,
      filters,
      ...sorter,
    });
  };

  return (
    <>
      {isAlert && (
        <Alert
          message={textAlert}
          type="success"
          banner
          closable
          afterClose={() => setIsAlert(false)}
        />
      )}

      <Card
        title={
          <Space>
            <GiftOutlined />
            <b>Prize Claim</b>
            <Tooltip title="Data klaim hadiah user dari hasil spin.">
              <InfoCircleOutlined />
            </Tooltip>
          </Space>
        }
        extra={
          <Button onClick={getData}>
            <ReloadOutlined /> Refresh
          </Button>
        }
      >
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} md={10}>
            <Search
              placeholder="Cari claim code, nama, email, mobile, atau hadiah..."
              allowClear
              enterButton={<SearchOutlined />}
              onSearch={(value) => {
                setSearch(value);
                setPage({
                  pagination: {
                    current: 1,
                    pageSize: 10,
                  },
                });
              }}
            />
          </Col>

          <Col xs={24} md={6}>
            <Select
              allowClear
              placeholder="Filter status"
              style={{ width: "100%" }}
              onChange={(value) => {
                setStatus(value || "");
                setPage({
                  pagination: {
                    current: 1,
                    pageSize: 10,
                  },
                });
              }}
              options={[
                { value: "pending", label: "Pending" },
                { value: "approved", label: "Approved" },
                { value: "rejected", label: "Rejected" },
                { value: "delivered", label: "Delivered" },
              ]}
            />
          </Col>
        </Row>

        <Table
          rowKey="prize_claim_id"
          columns={columns}
          dataSource={data}
          loading={loading}
          pagination={page.pagination}
          onChange={handleTableChange}
          scroll={{ x: 1500 }}
        />
      </Card>
    </>
  );
}

export default PrizeClaim;